import { IonIcon } from '@ionic/react';
import type { ButtonHTMLAttributes, ComponentProps, ReactNode } from 'react';
import { forwardRef } from 'react';
import { cn } from '../../lib/utils';

type IconType = ComponentProps<typeof IonIcon>['icon'];

export type ButtonVariant =
  | 'primary'
  | 'green'
  | 'gold'
  | 'outline'
  | 'ghost'
  | 'danger';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Fully rounded pill shape (default) vs. the rounded-xl card shape. */
  pill?: boolean;
  fullWidth?: boolean;
  /** Ionicon rendered before the label. */
  icon?: IconType;
  /** Ionicon rendered after the label. */
  iconEnd?: IconType;
  children?: ReactNode;
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'bg-[var(--fc-navy)] text-white hover:bg-[var(--ion-color-primary-shade)] active:opacity-90',
  green: 'bg-[var(--fc-green)] text-white hover:opacity-95 active:opacity-90',
  gold: 'text-[#3c2f06] hover:opacity-95 active:opacity-90 [background:var(--fc-gold-gradient)]',
  outline: 'bg-white text-[var(--fc-navy)] border-[1.5px] border-[var(--fc-navy)] hover:bg-slate-50',
  ghost: 'bg-[#eef1f6] text-[var(--fc-navy)] hover:bg-[#e4e9f1]',
  danger: 'bg-[#fdeeee] text-[#c0392b] hover:bg-[#fbe1e1]',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'min-h-9 gap-1.5 px-3.5 py-2 text-[13px]',
  md: 'min-h-11 gap-2 px-4 py-2.5 text-sm',
  lg: 'min-h-[52px] gap-2 px-5 py-3.5 text-[15px]',
};

/**
 * Shared Futyclub button. Native `<button>` styled with the brand tokens so it
 * stays consistent across every redesigned surface (cards, modals, headers).
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      pill = true,
      fullWidth = false,
      icon,
      iconEnd,
      className,
      children,
      type = 'button',
      ...rest
    },
    ref
  ) => (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center justify-center font-bold leading-none transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fc-navy)] focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50',
        pill ? 'rounded-full' : 'rounded-xl',
        fullWidth && 'w-full',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        className
      )}
      {...rest}
    >
      {icon && <IonIcon icon={icon} className="shrink-0 text-base" aria-hidden="true" />}
      {children != null && <span className="min-w-0 truncate">{children}</span>}
      {iconEnd && <IonIcon icon={iconEnd} className="shrink-0 text-base" aria-hidden="true" />}
    </button>
  )
);

Button.displayName = 'Button';
